/* eslint-disable no-param-reassign */
import { Module } from 'vuex'
import { PostActionState, CopyOrTransferPostResult, RootState } from 'surface/xstore/types'
import PadletApi from 'surface/padlet_api'
import { getClientId, copyPost, transferPost } from '../helpers/post'
import window from 'bits/global'
import { __ } from 'bits/intl'
import { Cid } from 'types'

function getStartingExpandedPostCid () : Cid | null {
  if (window.location.pathname.split('/').length === 5) {
    return 'c' + window.location.pathname.split('/')[4]
  }
  return null
}

const postActionModule: Module<PostActionState, RootState> = {
  namespaced: true,
  state: {
    // Only one media player is allowed at a time.
    currentlyPlayingPostCid: null,
    zoomedPostCid: null,
    zoomedLink: null,
    expandedPostCid: null,
    postUnderActionCid: null,
    xTransferPostPanel: false,
    postBeingTransferredCid: null,
    transferPostActivePanel: 'main',
    xCopyPostPanel: false,
    postBeingCopiedCid: null,
    copyPostActivePanel: 'main',
    destinationWallSections: [],
    destinationWallSectionsFetched: false,
    copyOrTransferPostResult: null,
  },
  getters: {
    zoomedPostCid: state => state.zoomedPostCid,
    zoomedLink: state => state.zoomedLink,
    expandedPostCid: state => state.expandedPostCid,
    postUnderActionCid: state => state.postUnderActionCid,
    xTransferPostPanel: state => state.xTransferPostPanel,
    postBeingTransferredCid: state => state.postBeingTransferredCid,
    transferPostActivePanel: state => state.transferPostActivePanel,
    copyOrTransferPostResult: state => state.copyOrTransferPostResult,
    xCopyPostPanel: state => state.xCopyPostPanel,
    postBeingCopiedCid: state => state.postBeingCopiedCid,
    copyPostActivePanel: state => state.copyPostActivePanel,
    destinationWallSections: state => state.destinationWallSections,
    destinationWallSectionsFetched: state => state.destinationWallSectionsFetched,
    hasPostUnderActionWithConnections: (state, getters, rootState, rootGetters): boolean => {
      if (!getters.postUnderAction) return false
      const postId = getters.postUnderAction.id
      return rootGetters['postConnection/connections'].filter((cxn): boolean => {
        return cxn.from_wish_id === postId || cxn.to_wish_id === postId
      }).length > 0
    },
    isPostUnderActionUserAuthored: (state, getters, rootState): boolean => {
      if (!getters.postUnderAction) return false
      return getters.postUnderAction.author_id === rootState.user.id
    },
    canEditPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.canIEdit || (rootGetters.canIWrite && getters.isPostUnderActionUserAuthored)
    },
    canDeletePostUnderAction: (state, getters): boolean => getters.canEditPostUnderAction,
    canTransferPostUnderAction: (state, getters): boolean => getters.canEditPostUnderAction,
    canCopyPostUnderAction: (state, getters, rootState, rootGetters): boolean => rootGetters.amIRegistered,
    canConnectPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.isCanvas && rootGetters.canIWrite
    },
    canDisconnectPostUnderAction: (state, getters): boolean => {
      return getters.canConnectPostUnderAction && getters.hasPostUnderActionWithConnections
    },
    canChangePostStackOrder: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.isCanvas && rootGetters.canIEdit
    },
    doesPostUnderActionHaveAttachment: (state, getters): boolean => {
      return getters.postUnderAction && getters.postUnderAction.attachment && getters.postUnderAction.attachment !== ''
    },
    canChangeCoverToPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.canIChangeCoverPost && getters.doesPostUnderActionHaveAttachment
    },
    canChangePostUnderActionLocation: (_state, _getters, _rootState, rootGetters): boolean => {
      return rootGetters.isMap && rootGetters.canIEdit
    },
    isPostUnderActionPreviewPost: (state, getters, rootState, rootGetters): boolean => {
      return getters.postUnderAction && getters.postUnderAction.id === rootGetters.coverPostId
    },
    expandedPostServerId: (state, getters, rootState) => {
      return (state.expandedPostCid && rootState.post.postEntitiesByCid[state.expandedPostCid])
        ? rootState.post.postEntitiesByCid[state.expandedPostCid].id : null
    },
    expandedPostFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/post${getters.expandedPostServerId}`
    },
    postUnderAction: (state, getters, rootState, rootGetters) => {
      if (!state.postUnderActionCid) return null
      const postEntitiesByCid = rootGetters['post/postEntitiesByCid']
      return postEntitiesByCid[state.postUnderActionCid]
    },
  },
  mutations: {
    SHOW_POST_ACTION_MENU (state, postCid) {
      state.postUnderActionCid = postCid
    },
    HIDE_POST_ACTION_MENU (state) {
      state.postUnderActionCid = null
    },
    ZOOM_POST (state, { zoomedPostCid, zoomedLink }): void {
      state.currentlyPlayingPostCid = zoomedPostCid
      state.zoomedPostCid = zoomedPostCid
      state.zoomedLink = zoomedLink
    },
    UNZOOM_POST (state): void {
      if (state.currentlyPlayingPostCid === state.zoomedPostCid) {
        state.currentlyPlayingPostCid = null
      }
      state.zoomedPostCid = null
      state.zoomedLink = null
    },
    EXPAND_POST (state, postCid): void {
      state.currentlyPlayingPostCid = postCid
      state.expandedPostCid = postCid
    },
    UNEXPAND_POST (state): void {
      if (state.currentlyPlayingPostCid === state.expandedPostCid) {
        state.currentlyPlayingPostCid = null
      }
      state.expandedPostCid = null
    },
    START_POST_TRANSFER (state, transferpostCid) {
      state.xTransferPostPanel = true
      state.postBeingTransferredCid = transferpostCid
    },
    END_POST_TRANSFER (state) {
      state.xTransferPostPanel = false
      state.postBeingTransferredCid = null
      state.transferPostActivePanel = 'main'
      state.copyOrTransferPostResult = null
    },
    UPDATE_POST_TRANSFER (state: PostActionState, copyXferPostResult: CopyOrTransferPostResult): void {
      if (copyXferPostResult.status !== 'error') state.postBeingTransferredCid = null
      state.copyOrTransferPostResult = copyXferPostResult
    },
    POST_TRANSFER_NAVIGATE (state, panel) {
      state.transferPostActivePanel = panel
    },
    START_POST_COPY (state, copypostCid) {
      state.xCopyPostPanel = true
      state.postBeingCopiedCid = copypostCid
    },
    END_POST_COPY (state) {
      state.xCopyPostPanel = false
      state.postBeingCopiedCid = null
      state.copyPostActivePanel = 'main'
      state.copyOrTransferPostResult = null
    },
    UPDATE_POST_COPY (state: PostActionState, copyXferPostResult: CopyOrTransferPostResult): void {
      state.copyOrTransferPostResult = copyXferPostResult
    },
    POST_COPY_NAVIGATE (state, panel) {
      state.copyPostActivePanel = panel
    },
    FETCH_DESTINATION_WALL_SECTIONS (state) {
      state.destinationWallSections = []
      state.destinationWallSectionsFetched = false
    },
    FETCHED_DESTINATION_WALL_SECTIONS (state, sections) {
      state.destinationWallSections = sections
      state.destinationWallSectionsFetched = true
    },
    SET_CURRENTLY_PLAYING_POST (state, cid): void {
      state.currentlyPlayingPostCid = cid
    },
    UNSET_CURRENTLY_PLAYING_POST (state, cid): void {
      if (state.currentlyPlayingPostCid === cid) {
        state.currentlyPlayingPostCid = null
      }
    },
  },
  actions: {
    /* ------------
     * SURFACE STATE MODIFICATION OPERATIONS
     * ------------ */
    showPostActionMenu ({ commit }, { postCid }) {
      commit('SHOW_POST_ACTION_MENU', postCid)
    },
    hidePostActionMenu ({ commit }) {
      commit('HIDE_POST_ACTION_MENU')
    },
    zoomPost ({ commit }, { zoomedPostCid, zoomedLink }) {
      commit('UNEXPAND_POST')
      commit('ZOOM_POST', { zoomedPostCid, zoomedLink })
    },
    unzoomPost ({ commit }) {
      commit('UNEXPAND_POST')
      commit('UNZOOM_POST')
    },
    expandPost ({
      commit, rootState, dispatch
    }, { postCid }) {
      commit('UNZOOM_POST')
      if (!rootState.isApp) commit('EXPAND_POST', postCid)
      const post = rootState.post.postEntitiesByCid[postCid]
      dispatch('nativeApp/expandPost', post, { root: true })
    },
    unexpandPost ({ commit, dispatch }) {
      commit('UNEXPAND_POST')
      dispatch('nativeApp/unexpandPost', null, { root: true })
    },
    startPostTransfer ({ commit }, { transferPostCid }) {
      commit('START_POST_TRANSFER', transferPostCid)
    },
    endPostTransfer ({ commit }) {
      commit('END_POST_TRANSFER')
    },
    actuallyTransferPost ({ dispatch }, transferParams): void {
      transferPost({ dispatch }, transferParams)
    },
    async updatePostTransferred ({ commit, dispatch, rootGetters, rootState }, { transferredPostCid, error, newPostData, isDestinationNewWall, isDestinationThisWall }) {
      const result: CopyOrTransferPostResult = {
        status: 'success',
        operation: 'transfer',
        isDestinationThisWall,
        isDestinationNewWall,
      }
      if (error) {
        result.status = 'error'
        result.text = __(`Sorry, we failed to transfer your post.`) + ' ' + (error.error || '')
      } else {
        let wallInfo = rootState.wall
        if (!isDestinationThisWall) {
          wallInfo = await PadletApi.Wall.read({ id: newPostData.wall_id })
          // Here we remove the transferred post from the surface.
          dispatch('removePost', { cid: transferredPostCid }, { root: true })
        } else {
          dispatch('addPostCopiedOrMovedToCurrent', { newPostData })
        }
        result.url = wallInfo.links.show
        result.text = __('Post transferred successfully.')
      }
      commit('UPDATE_POST_TRANSFER', result)
    },
    postTransferNavigate ({ commit }, { panel }) {
      commit('POST_TRANSFER_NAVIGATE', panel)
    },
    startPostCopy ({ commit }, { copyPostCid }) {
      commit('START_POST_COPY', copyPostCid)
    },
    endPostCopy ({ commit }) {
      commit('END_POST_COPY')
    },
    actuallyCopyPost ({ dispatch }, copyParams): void {
      copyPost({ dispatch }, copyParams)
    },
    async updatePostCopied ({ commit, rootGetters, rootState, dispatch }, {
      error,
      newPostData,
      isDestinationNewWall,
    }): Promise<void> {
      const result: CopyOrTransferPostResult = {
        status: 'success',
        operation: 'copy',
        isDestinationThisWall: false,
        isDestinationNewWall,
      }
      if (error) {
        result.status = 'error'
        result.text = __(`Sorry, we failed to copy your post.`) + ' ' + (error.error || '')
      } else {
        result.isDestinationThisWall = !isDestinationNewWall && rootGetters.wallId === newPostData.wall_id
        let wallInfo = rootState.wall
        if (result.isDestinationThisWall) {
          dispatch('addPostCopiedOrMovedToCurrent', { newPostData })
        } else {
          wallInfo = await PadletApi.Wall.read({ id: newPostData.wall_id })
        }
        result.url = wallInfo.links.show
        result.text = __('Post copied successfully.')
      }
      commit('UPDATE_POST_COPY', result)
    },
    addPostCopiedOrMovedToCurrent ({ dispatch }, { newPostData }) {
      dispatch('addPost', { post: newPostData }, { root: true })
    },
    postCopyNavigate ({ commit }, { panel }) {
      commit('POST_COPY_NAVIGATE', panel)
    },
    async fetchDestinationWallSections ({ commit }, { wallId }) {
      commit('FETCH_DESTINATION_WALL_SECTIONS')
      const sections = await PadletApi.WallSection.readAll({
        wall: {
          id: wallId,
        },
      })
      commit('FETCHED_DESTINATION_WALL_SECTIONS', sections)
    },
    setCurrentlyPlayingPost ({ commit }, { cid }): void {
      commit('SET_CURRENTLY_PLAYING_POST', cid)
    },
    unsetCurrentlyPlayingPost ({ commit }, { cid }): void {
      commit('UNSET_CURRENTLY_PLAYING_POST', cid)
    },
    /* ------------
     * REMOTE
     * ------------ */
    deletePostRemote ({ state, dispatch }, post): void {
      if (post) {
        const postCid = getClientId(post)
        if (postCid === state.postBeingTransferredCid) {
          dispatch('endPostTransfer')
        }
        if (postCid === state.postBeingCopiedCid) {
          dispatch('endPostCopy')
        }
        if (postCid === state.postUnderActionCid) {
          dispatch('hidePostActionMenu')
        }
        if (postCid === state.zoomedPostCid) {
          dispatch('unzoomPost')
        }
        if (postCid === state.expandedPostCid) {
          dispatch('unexpandPost')
        }
      }
    },
    // TODO we should not need this method, ideally. Getters and watches should take care of this.
    recalibrate ({ state, rootState, dispatch }): void {
      const postEntitiesByCid = rootState.post.postEntitiesByCid
      if (state.postBeingTransferredCid && !(state.postBeingTransferredCid in postEntitiesByCid)) {
        dispatch('endPostTransfer')
      }
      if (state.postBeingCopiedCid && !(state.postBeingCopiedCid in postEntitiesByCid)) {
        dispatch('endPostCopy')
      }
      if (state.postUnderActionCid && !(state.postUnderActionCid in postEntitiesByCid)) {
        dispatch('hidePostActionMenu')
      }
      if (state.zoomedPostCid && !(state.zoomedPostCid in postEntitiesByCid)) {
        dispatch('unzoomPost')
      }
      if (state.expandedPostCid && !(state.expandedPostCid in postEntitiesByCid)) {
        dispatch('unexpandPost')
      }
    },
  },
}

export default postActionModule
